import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AbTestWebservice } from '@core/services/abtest.webservice';
import { environment } from '@environment';
import { Action, NgxsOnInit, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { GetAbTestVersion } from '@stores/abtest/abtest.action';
import { JwtState } from '@stores/jwt/jwt.state';
import { UserType } from '@wizbii/webservices';
import { CookieService } from 'ngx-cookie-service';
import { filter, switchMap, tap } from 'rxjs/operators';

export interface AbTestStateModel {
  userType: UserType;
  version: string;
  campaignId: string;
}

export const AbTestStateToken = new StateToken<AbTestStateModel>('abtest');

@State({
  name: AbTestStateToken,
  defaults: null,
})
@Injectable()
export class AbTestState implements NgxsOnInit {
  @Selector([AbTestStateToken])
  static version(abTestState: AbTestStateModel): string {
    return abTestState?.version;
  }

  @Selector([AbTestStateToken])
  static campaignId(abTestState: AbTestStateModel): string {
    return abTestState?.campaignId;
  }

  constructor(
    private readonly store: Store,
    private readonly cookieService: CookieService,
    private readonly abTestWebservice: AbTestWebservice,
    @Inject(PLATFORM_ID) private readonly platformId: string
  ) {}

  ngxsOnInit(ctx: StateContext<AbTestStateModel>) {
    const abtest = ctx.getState();

    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.store
      .select(JwtState.info)
      .pipe(
        filter(
          (jwtInfo) =>
            !abtest?.campaignId ||
            !abtest?.version ||
            abtest?.campaignId !== environment.abTest.campaignId ||
            (abtest?.userType === 'visitor' && !!jwtInfo)
        ),
        switchMap(() => ctx.dispatch(new GetAbTestVersion()))
      )
      .subscribe();
  }

  @Action(GetAbTestVersion)
  getAbTestVersion(ctx: StateContext<AbTestStateModel>) {
    const uuid = this.cookieService.get('uniqUserId');
    const userId = this.store.selectSnapshot(JwtState.userId);

    return this.abTestWebservice
      .getVersion(environment.abTest.campaignId, userId ? 'member' : 'visitor', userId ?? uuid)
      .pipe(
        tap((abtestResp) => {
          ctx.setState({
            version: abtestResp.value,
            userType: userId ? 'member' : 'visitor',
            campaignId: environment.abTest.campaignId,
          });
        })
      );
  }
}
